/**
 * Cesium entity lookups used across the path-mode panel + (future) hover
 * popup. Plain functions — no component context required.
 */

import { Cartesian3, type Entity } from 'cesium';
import { get } from 'svelte/store';

import { useCesiumViewer } from '../cesium-viewer';
import { useDataSources } from '../cesium-datasources';
import { mapModeStore } from '../map-mode-store';
import { ENTITY_ID_PREFIX } from '$lib/utils/constants';
import { getLoadCaseLineEntities } from './pathToolLoadCases.svelte';

function planAndLiveDataSources() {
	const { mapdataDataSource } = useDataSources();
	const planDataSource = get(mapModeStore).activeDataSource;
	const list: { entities: { getById: (id: string) => Entity | undefined } }[] = [];
	if (mapdataDataSource) list.push(mapdataDataSource);
	if (planDataSource) list.push(planDataSource);
	return list;
}

/** First match across plan + live datasources. */
export function findLineEntity(lineId: number): Entity | undefined {
	const entityId = `${ENTITY_ID_PREFIX.LINE}${lineId}`;
	for (const source of planAndLiveDataSources()) {
		const entity = source.entities.getById(entityId);
		if (entity) return entity;
	}
	return undefined;
}

/**
 * Plan/live original plus every loadcase's CZML duplicate. Section colours
 * are applied to all returned entities so styling persists regardless of
 * which datasource is currently visible.
 */
export function findAllLineEntities(lineId: number): Entity[] {
	const entityId = `${ENTITY_ID_PREFIX.LINE}${lineId}`;
	const result: Entity[] = [];
	for (const source of planAndLiveDataSources()) {
		const entity = source.entities.getById(entityId);
		if (entity) result.push(entity);
	}
	result.push(...getLoadCaseLineEntities(entityId));
	return result;
}

/** First match across plan + live datasources. */
export function findMastEntity(mastId: number): Entity | undefined {
	const entityId = `${ENTITY_ID_PREFIX.MASTPOINT}${mastId}`;
	for (const source of planAndLiveDataSources()) {
		const entity = source.entities.getById(entityId);
		if (entity) return entity;
	}
	return undefined;
}

/**
 * Straight-line distance between the first and last positions of the
 * spanLineset's first polyline. Returns `null` when the entity or its
 * positions can't be resolved (e.g. tile not yet loaded).
 */
export function spanLinesetLength(span: { lines?: { id: number }[] }): number | null {
	const firstLineId = span.lines?.[0]?.id;
	if (firstLineId == null) return null;
	const entity = findLineEntity(firstLineId);
	if (!entity?.polyline?.positions) return null;
	const viewer = useCesiumViewer();
	if (!viewer) return null;
	const positions = entity.polyline.positions.getValue(viewer.clock.currentTime) as
		| Cartesian3[]
		| undefined;
	if (!positions || positions.length < 2) return null;
	return Cartesian3.distance(positions[0], positions[positions.length - 1]);
}
